"use strict";

import {
    Button,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    CircularProgress,
    Pagination,
    Stack,
    Typography,
    TextField,
    InputAdornment,
    MenuItem,
} from "@mui/material";
import React, { useEffect, useState, useRef, useCallback, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getUsers } from "../features/users/api";
import { Store } from "../Store";
import { fetchManagedTeams } from "../utils/teamRequests";

const UserListScreen = () => {
    const { state } = useContext(Store);
    const { userInfo } = state;
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const searchInputRef = useRef(null);
    const [pagination, setPagination] = useState({
        total: 0,
        page: 1,
        pageSize: 10,
        totalPages: 0,
    });
    const [teams, setTeams] = useState([]);
    const [teamsLoading, setTeamsLoading] = useState(false);
    const [teamsError, setTeamsError] = useState(null);
    const tableCellSx = { py: 1 };

    // Debounce search term
    useEffect(() => {
        const timerId = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500); // 500ms delay

        return () => {
            clearTimeout(timerId);
        };
    }, [searchTerm]);

    const fetchUsers = useCallback(async (
        page = 1,
        search = "",
        pageSizeParam
    ) => {
        const pageSize = pageSizeParam ?? pagination.pageSize;
        console.assert(
            Number.isInteger(pageSize) && pageSize > 0,
            "Page size must be a positive integer"
        );

        try {
            // Start loading and request the user list from the API.
            setLoading(true);
            const response = await getUsers({
                page,
                search,
                pageSize,
            });

            // Normalize and store the user results.
            const usersData = Array.isArray(response.data?.users)
                ? response.data.users
                : [];
            setUsers(usersData);

            // Update pagination metadata for the table footer.
            if (response.data.pagination) {
                setPagination((prev) => ({
                    ...prev,
                    ...response.data.pagination,
                    pageSize,
                }));
            } else {
                setPagination((prev) => ({ ...prev, page, pageSize }));
            }

            // Clear any prior error states after a successful request.
            setError(null);

            // Restore focus to the search input when it was already active.
            if (document.activeElement === searchInputRef.current) {
                setTimeout(() => {
                    searchInputRef.current?.focus();
                }, 0);
            }
        } catch (err) {
            console.error("Error fetching users:", err);
            setError("Failed to load users");
        } finally {
            setLoading(false);
        }
    }, [pagination.pageSize]);

    const fetchTeams = useCallback(async () => {
        // Skip the request when no user context is available yet.
        if (!userInfo?.id) {
            setTeams([]);
            return;
        }

        try {
            // Request the teams list for the current requester.
            setTeamsLoading(true);
            const teamsData = await fetchManagedTeams({ requesterUserId: userInfo.id });

            // Normalize and store the teams list for lookup usage.
            setTeams(teamsData);
            setTeamsError(null);
        } catch (err) {
            console.error("Error fetching teams:", err);
            setTeams([]);
            setTeamsError("Failed to load teams");
        } finally {
            setTeamsLoading(false);
        }
    }, [userInfo?.id]);

    useEffect(() => {
        // Refresh the team list when the requester changes.
        fetchTeams();
    }, [fetchTeams]);

    useEffect(() => {
        fetchUsers(1, debouncedSearchTerm);
    }, [debouncedSearchTerm, fetchUsers]);

    // Handle page change
    const handlePageChange = (event, newPage) => {
        fetchUsers(newPage, debouncedSearchTerm);
    };

    // Handle page size change
    const handlePageSizeChange = (event) => {
        const newSize = Number(event.target.value);
        setPagination((prev) => ({ ...prev, page: 1, pageSize: newSize }));
        // Fetch first page with new page size
        fetchUsers(1, debouncedSearchTerm, newSize);
    };

    // Handle search input change
    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        // We don't reset page here anymore, as the actual search will happen with debounce
    };

    // Clear search with focus preservation
    const clearSearch = useCallback(() => {
        setSearchTerm("");
        setTimeout(() => {
            searchInputRef.current?.focus();
        }, 0);
    }, []);

    const teamsByUserId = useMemo(() => {
        // Build a lookup of user IDs to the teams they belong to.
        const lookup = {};
        teams.forEach((team) => {
            const teamLabel = team?.team_name || "Unnamed Team";
            if (team?.project_manager) {
                const pmId = team.project_manager;
                lookup[pmId] = lookup[pmId] || [];
                lookup[pmId].push(`${teamLabel} (PM)`);
            }

            const members = Array.isArray(team?.members) ? team.members : [];
            members.forEach((memberId) => {
                lookup[memberId] = lookup[memberId] || [];
                lookup[memberId].push(teamLabel);
            });
        });
        return lookup;
    }, [teams]);

    const navigate = useNavigate();

    const deleteHandler = (user) => {
        // Implement delete functionality here
        console.log("Delete user:", user);
    };

    if (loading) {
        return (
            <Grid
                container
                justifyContent="center"
                alignItems="center"
                style={{ height: "50vh" }}
            >
                <CircularProgress />
            </Grid>
        );
    }

    if (error) {
        return (
            <Grid
                container
                justifyContent="center"
                alignItems="center"
                style={{ height: "50vh" }}
            >
                <div>{error}</div>
            </Grid>
        );
    }

    return (
        <Grid item container justifyContent="center">
            <Grid item container xs={7} marginTop={7} direction="column">
                <Grid
                    item
                    container
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                >
                    <Grid
                        item
                        sx={{
                            fontSize: "var(--font-size-header)",
                            fontWeight: "600",
                        }}
                    >
                        Users
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search users..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            size="small"
                            inputRef={searchInputRef}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Button
                                            size="small"
                                            onClick={clearSearch}
                                            disabled={!searchTerm}
                                        >
                                            Clear
                                        </Button>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                </Grid>
                <Grid item>
                    <Table size="small" sx={{ minWidth: 650, bgcolor: "white" }}>
                        <TableHead bgcolor="#5570dc">
                            <TableRow>
                                <TableCell sx={tableCellSx}>ID</TableCell>
                                <TableCell sx={tableCellSx}>NAME</TableCell>
                                <TableCell sx={tableCellSx}>EMAIL</TableCell>
                                <TableCell sx={tableCellSx}>Teams</TableCell>
                                <TableCell sx={tableCellSx}>Admin</TableCell>
                                <TableCell sx={tableCellSx}>ACTIONS</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users && users.length > 0 ? (
                                users.map((user, index) => {
                                    // Calculate the actual continuous index based on the current page and page size
                                    const actualIndex =
                                        (pagination.page - 1) *
                                            pagination.pageSize +
                                        index +
                                        1;

                                    return (
                                        <TableRow key={user.id}>
                                            <TableCell sx={tableCellSx}>{actualIndex}</TableCell>
                                            <TableCell sx={tableCellSx}>{user.name}</TableCell>
                                            <TableCell sx={tableCellSx}>{user.email}</TableCell>
                                            <TableCell sx={tableCellSx}>
                                                {teamsLoading ? (
                                                    <>Loading...</>
                                                ) : teamsError ? (
                                                    <>Unavailable</>
                                                ) : (teamsByUserId[user.id] || []).length > 0 ? (
                                                    teamsByUserId[user.id].join(", ")
                                                ) : (
                                                    <>None</>
                                                )}
                                            </TableCell>
                                            <TableCell sx={tableCellSx}>
                                                {user.isAdmin ? (
                                                    <>True</>
                                                ) : (
                                                    <>False</>
                                                )}
                                            </TableCell>
                                            <TableCell sx={tableCellSx}>
                                                <Button
                                                    color="primary"
                                                    onClick={() =>
                                                        navigate(
                                                            `/admin/user/${user.id}`
                                                        )
                                                    }
                                                >
                                                    Edit
                                                </Button>
                                                &nbsp;
                                                <Button
                                                    color="error"
                                                    onClick={() =>
                                                        deleteHandler(user)
                                                    }
                                                >
                                                    Delete
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={tableCellSx}>
                                        No users found
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Grid>

                {/* Pagination */}
                <Grid
                    item
                    mt={3}
                    container
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Typography variant="body2">
                        Showing {users.length} of {pagination.total} users
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Pagination
                            count={pagination.totalPages}
                            page={pagination.page}
                            onChange={handlePageChange}
                            color="primary"
                            showFirstButton
                            showLastButton
                        />
                        <TextField
                            select
                            size="small"
                            value={pagination.pageSize}
                            onChange={handlePageSizeChange}
                            sx={{ width: 80 }}
                        >
                            <MenuItem value={5}>5</MenuItem>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={25}>25</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                        </TextField>
                    </Stack>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default UserListScreen;
